import React, { useState } from 'react';
import { Mountain, Globe, BarChart3, Users, TrendingUp, Smartphone, MessageCircle, ArrowRight, CheckCircle, Eye, Search, Code, Download, Bike, Car, Backpack, Star, Zap, Target, Layers } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const PARTNERSHIP_TIERS = [
  {
    name: 'Presenting Partner',
    price: '₹15L — ₹25L',
    priceSub: '/year',
    color: '#D4A853',
    description: 'Full co-branded experience. Your brand becomes synonymous with a signature Himalayan journey.',
    features: [
      'Co-branded itinerary page (e.g., /adventures/your-brand-spiti)',
      'Brand identity integrated into the experience (nav, hero, colours)',
      '4 community departures per year with your brand activation',
      'Dedicated Instagram creative series (12 posts/year)',
      'Lead generation — all booking inquiries shared',
      'Quarterly brand impact report with traveller demographics',
      'Logo on GoHimalayan homepage and itinerary catalogue',
      'Press release and co-authored launch story',
    ],
    ideal: 'Royal Enfield, Mahindra Adventure, Volkswagen, Mercedes-Benz',
    icon: Star,
  },
  {
    name: 'Experience Partner',
    price: '₹5L — ₹10L',
    priceSub: '/year',
    color: '#8B9DAF',
    description: 'Your brand powers a specific element of the journey — gear, transport, or on-ground activation.',
    features: [
      '"Powered by [Brand]" badge on 2 itinerary pages',
      'Gear/product placement at curated homestays',
      '2 community departures with on-ground activation',
      'Social media content collaboration (6 posts/year)',
      'Lead sharing on relevant bookings',
      'Bi-annual brand report',
    ],
    ideal: 'Decathlon, The North Face, Wildcraft, Bira 91',
    icon: Layers,
    highlight: true,
  },
  {
    name: 'Content Partner',
    price: '₹2L — ₹4L',
    priceSub: '/year',
    color: '#A8B8C8',
    description: 'Brand visibility through GoHimalayan\'s content engine — social media, storytelling pages, and SEO.',
    features: [
      'Brand mention in 4 itinerary descriptions',
      'Product inclusion in Instagram creative series',
      'Backlink from GoHimalayan to your brand',
      'Annual brand report',
    ],
    ideal: 'Outdoor brands, travel gear, F&B, local artisan brands',
    icon: Eye,
  },
];

const WHAT_WE_BRING = [
  { icon: Mountain, value: '100+', label: 'Curated stays and growing', sub: '13 Himalayan states mapped' },
  { icon: Search, value: 'AI', label: 'Natural language search + trip planner', sub: 'Travellers describe, we plan' },
  { icon: Eye, value: '1,080px', label: 'Instagram creative engine', sub: 'Automated per-property cards' },
  { icon: Globe, value: '13', label: 'State tourism pages live', sub: 'B2G pitch decks for 3 states' },
  { icon: Users, value: '8-12', label: 'Community departure groups', sub: 'Curated, intimate, repeatable' },
  { icon: Smartphone, value: 'WhatsApp', label: 'Zero-friction booking', sub: 'No app, no payment gateway' },
];

const WHY_BRANDS = [
  { title: 'The Himalayan audience is premium', desc: 'Himalayan travellers skew 25-45, urban, high-disposable-income. They buy gear, they own SUVs, they\'re brand-conscious. They\'re your customer — already in the mountains.' },
  { title: 'Authenticity beats advertising', desc: 'A co-branded itinerary with curated homestays feels like editorial content, not an ad. Your brand is embedded in the experience, not interrupting it.' },
  { title: 'No one else owns this layer', desc: 'OTAs sell rooms. Adventure companies sell packages. GoHimalayan owns the stays + the storytelling + the community. That\'s the activation layer brands need.' },
  { title: 'Repeatable, measurable, scalable', desc: 'Fixed departure dates mean repeatable activations. WhatsApp bookings mean traceable leads. AI content means your brand shows up in search. Every rupee is measurable.' },
];

const BRAND_IDEAS = [
  { brand: 'Royal Enfield', icon: Bike, route: 'Spiti Circuit', desc: '7-day motorcycle expedition, Manali → Kaza → Nako. RE Himalayan 450s, curated rider homestays, Kunzum La at 4,551m.', link: '/adventures/royal-enfield-spiti', live: true },
  { brand: 'Mahindra Adventure', icon: Car, route: 'Kumaon Heritage Drive', desc: '5-day Thar expedition through Almora → Binsar → Mukteshwar. Off-road trails, heritage bungalows, 300km Himalayan wall.', link: null, live: false },
  { brand: 'Volkswagen', icon: Car, route: 'Valley Explorer', desc: 'Luxury 6-day Tiguan drive through Tirthan → Jibhi → Kullu. Scenic valley roads, boutique stays, artisanal food trails.', link: null, live: false },
  { brand: 'Decathlon', icon: Backpack, route: 'Meghalaya Trek + Stay', desc: '5-day trek circuit with base camps at GoHimalayan homestays. Root bridge treks, gear testing, community sport events.', link: null, live: false },
];

export default function PitchBrands() {
  const [activeTier, setActiveTier] = useState(0);

  return (
    <div className="min-h-screen bg-white" data-testid="pitch-brands-page">
      <div className="fixed top-6 right-6 z-50 print:hidden">
        <button onClick={() => window.print()} className="flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-md border border-stone-200 rounded-full shadow-lg text-stone-700 text-xs font-medium tracking-wider uppercase hover:bg-white hover:shadow-xl transition-all" data-testid="download-pdf-btn"><Download className="w-4 h-4" />Download PDF</button>
      </div>

      {/* TITLE */}
      <section className="min-h-screen flex flex-col justify-center items-center px-6 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #0a0a0a 0%, #1a1a1a 40%, #2a1f0a 70%, #0a0a0a 100%)' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(60deg, transparent, transparent 25px, white 25px, white 26px)' }} />
        <div className="relative z-10 text-center max-w-4xl">
          <div className="flex items-center justify-center gap-3 mb-8"><Mountain className="w-10 h-10 text-white/80" strokeWidth={2} /><span className="text-2xl font-bold text-white/90 tracking-tight" style={pf}>GoHimalayan</span></div>
          <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A853]/20 bg-[#D4A853]/5 mb-8"><p className="text-[#D4A853]/60 text-xs tracking-[0.3em] uppercase">Brand Partnership Program</p></div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light italic leading-tight mb-8" style={pf}>Your brand.<br />The Himalayas.<br />An experience people remember.</h1>
          <p className="text-white/40 text-lg max-w-2xl mx-auto mb-12" style={pf}>GoHimalayan is the Himalayan hospitality layer — a growing network of curated stays, AI-powered discovery, and community departures across 13 states. We're looking for brands that belong in the mountains.</p>
          <div className="flex items-center justify-center gap-6 text-white/20 text-xs tracking-widest uppercase"><span>Confidential</span><div className="w-1 h-1 rounded-full bg-white/20" /><span>2026</span><div className="w-1 h-1 rounded-full bg-white/20" /><span>gohimalayan.com</span></div>
        </div>
      </section>

      {/* WHY BRANDS NEED THIS */}
      <section className="py-24 px-6 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">The Opportunity</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>The Himalayan traveller is your customer.<br />You just haven't met them there yet.</h2>
          <p className="text-stone-500 text-lg max-w-3xl mb-16" style={pf}>5.95 Cr tourists visited Uttarakhand alone last year. They ride Royal Enfields, drive Thars, wear North Face, and book boutique stays. The mountains are where premium brands should live — not on billboards, but inside the experience itself.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {WHY_BRANDS.map((w, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-stone-100 hover:shadow-md transition-shadow">
                <h3 className="text-base font-semibold text-stone-900 mb-2" style={pf}>{w.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE BRING */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">The Platform</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>GoHimalayan is the activation layer<br />brands have been missing.</h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>OTAs sell rooms. Adventure agencies sell packages. GoHimalayan owns the stays, the storytelling, the search, and the community. That combination is what makes brand integration authentic and measurable.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WE_BRING.map((w, i) => (
              <div key={i} className="bg-stone-50 rounded-xl p-5 border border-stone-100">
                <w.icon className="w-5 h-5 text-[#D4A853] mb-3" />
                <p className="text-xl font-bold text-stone-900 mb-1" style={pf}>{w.value}</p>
                <p className="text-sm text-stone-600">{w.label}</p>
                <p className="text-[10px] text-stone-400 mt-1">{w.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND IDEAS */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #f8f5f1 0%, #FAFAFA 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">What It Looks Like</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>Co-curated experiences.<br />Not sponsored posts.</h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>Each partnership creates a living, bookable experience page — not a banner ad. Travellers discover it through search, share it on WhatsApp, and book it directly.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {BRAND_IDEAS.map((b, i) => (
              <div key={i} className={`rounded-xl p-6 border ${b.live ? 'border-[#D4A853]/30 bg-[#D4A853]/5' : 'border-stone-100 bg-white'} hover:shadow-md transition-shadow`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center"><b.icon className="w-5 h-5 text-stone-600" /></div>
                  <div>
                    <p className="text-base font-semibold text-stone-900" style={pf}>{b.brand} × GoHimalayan</p>
                    <p className="text-xs text-stone-400">{b.route}</p>
                  </div>
                  {b.live && <span className="ml-auto text-[9px] bg-[#D4A853] text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Live Demo</span>}
                </div>
                <p className="text-sm text-stone-500 leading-relaxed mb-3">{b.desc}</p>
                {b.live && b.link && (
                  <a href={b.link} className="text-[#D4A853] text-xs font-semibold hover:underline flex items-center gap-1">
                    View live page <ArrowRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP TIERS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">Partnership Tiers</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>Three levels of integration.<br />Choose what fits your brand.</h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>From full co-branded expeditions to content-level visibility. Every tier includes measurable outcomes and direct lead sharing.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {PARTNERSHIP_TIERS.map((t, i) => (
              <div key={i} className={`rounded-xl p-8 border-2 transition-all cursor-pointer ${activeTier === i ? 'border-[#D4A853] shadow-lg scale-[1.02]' : 'border-stone-100 hover:border-stone-200'} bg-white`} onClick={() => setActiveTier(i)}>
                <t.icon className="w-6 h-6 mb-4" style={{ color: t.color }} />
                <h3 className="text-xl font-bold text-stone-900 mb-1" style={pf}>{t.name}</h3>
                <div className="flex items-baseline gap-1 mb-1"><span className="text-2xl font-bold text-stone-900">{t.price}</span><span className="text-sm text-stone-400">{t.priceSub}</span></div>
                <p className="text-xs text-stone-500 mb-1 italic">{t.description}</p>
                <p className="text-[10px] text-[#D4A853] font-medium mb-5">Ideal for: {t.ideal}</p>
                <ul className="space-y-2.5">{t.features.map((f, j) => (<li key={j} className="flex items-start gap-2 text-sm text-stone-600"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: t.color }} />{f}</li>))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(160deg, #0a0a0a 0%, #1a1a1a 40%, #2a1f0a 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-[#D4A853]/40 mb-4">The Process</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-16" style={pf}>From handshake to live page<br />in 4 weeks.</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Align', desc: 'Joint briefing. We understand your brand positioning, target audience, and campaign goals for the Himalayan context.' },
              { step: '02', title: 'Create', desc: 'GoHimalayan designs the co-branded itinerary, curates the stays, builds the experience page, and creates social content.' },
              { step: '03', title: 'Launch', desc: 'Experience page goes live. Community departures open for booking. Social campaign kicks off. PR co-authored.' },
              { step: '04', title: 'Report', desc: 'Quarterly impact reports — booking inquiries, social reach, traveller demographics, brand sentiment. Data you can act on.' },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm">
                <span className="text-[#D4A853] text-2xl font-bold" style={pf}>{s.step}</span>
                <h3 className="text-white text-base font-semibold mt-3 mb-2" style={pf}>{s.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSE */}
      <section className="min-h-[60vh] flex flex-col justify-center items-center px-6" style={{ background: 'linear-gradient(180deg, #0d1520 0%, #0a0a0a 50%, #2a1f0a 100%)' }}>
        <div className="text-center max-w-3xl">
          <Mountain className="w-12 h-12 text-white/20 mx-auto mb-8" strokeWidth={1.5} />
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-6" style={pf}>The mountains are waiting.<br />Your brand should be there.</h2>
          <div className="w-16 h-[1px] bg-white/15 mx-auto my-8" />
          <p className="text-white/50 text-base mb-1" style={pf}>GoHimalayan Partnerships</p>
          <p className="text-white/30 text-sm tracking-wider mb-6">partnerships@gohimalayan.com · +91 9871155963</p>
          <p className="text-white/15 text-xs tracking-wider mt-4">gohimalayan.com · Confidential</p>
        </div>
      </section>
    </div>
  );
}
